import React, { useCallback, useEffect, useRef, useState } from 'react'
import Tooltip from '@material-ui/core/Tooltip'

let dismissCurrentTooltip = null

const dismissTooltip = () => {
  if (dismissCurrentTooltip) dismissCurrentTooltip()
}

const handleKeyDown = event => {
  if (event.key === 'Escape') dismissTooltip()
}

const setDismissListeners = enabled => {
  const method = enabled ? 'addEventListener' : 'removeEventListener'
  document[method]('scroll', dismissTooltip, true)
  document[method]('pointerdown', dismissTooltip, true)
  document[method]('visibilitychange', dismissTooltip)
  document[method]('keydown', handleKeyDown)
  window[method]('blur', dismissTooltip)
  window[method]('resize', dismissTooltip)
  window[method]('pagehide', dismissTooltip)
}

const isDeliveryStatus = event =>
  Boolean(event.target.closest('[data-message-status]'))

export default function MessageTimestampTooltip({ children, title }) {
  const [open, setOpen] = useState(false)
  const timerRef = useRef(null)
  const ignoreMouseUntilRef = useRef(0)

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current)
    timerRef.current = null
    setOpen(false)
    if (dismissCurrentTooltip === dismiss) {
      dismissCurrentTooltip = null
      setDismissListeners(false)
    }
  }, [])

  useEffect(
    () => () => {
      clearTimeout(timerRef.current)
      if (dismissCurrentTooltip === dismiss) {
        dismissCurrentTooltip = null
        setDismissListeners(false)
      }
    },
    [dismiss]
  )

  const scheduleOpen = (event, interaction, delay) => {
    dismissTooltip()
    if (!title || isDeliveryStatus(event)) return

    const anchor = event.currentTarget
    dismissCurrentTooltip = dismiss
    setDismissListeners(true)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      const interactionEnded =
        (interaction === 'mouse' && !anchor.matches(':hover')) ||
        (interaction === 'focus' && !anchor.contains(document.activeElement))
      if (
        !anchor.isConnected ||
        document.hidden ||
        !document.hasFocus() ||
        interactionEnded
      ) {
        dismiss()
        return
      }
      setOpen(true)
    }, delay)
  }

  return (
    <Tooltip
      title={title}
      open={open}
      placement='top'
      disableHoverListener
      disableFocusListener
      disableTouchListener
      onMouseOver={event => {
        if (Date.now() < ignoreMouseUntilRef.current) return
        if (isDeliveryStatus(event)) {
          dismissTooltip()
        } else if (dismissCurrentTooltip !== dismiss) {
          scheduleOpen(event, 'mouse', 500)
        }
      }}
      onMouseLeave={dismiss}
      onFocus={event => scheduleOpen(event, 'focus', 500)}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) dismiss()
      }}
      onTouchStart={event => {
        ignoreMouseUntilRef.current = Date.now() + 1000
        scheduleOpen(event, 'touch', 700)
      }}
      onTouchMove={dismiss}
      onTouchCancel={dismiss}
      onTouchEnd={() => {
        ignoreMouseUntilRef.current = Date.now() + 1000
        clearTimeout(timerRef.current)
        if (open) {
          timerRef.current = setTimeout(dismiss, 700)
        } else {
          dismiss()
        }
      }}
      TransitionProps={{ timeout: { enter: 100, exit: 0 } }}
      PopperProps={{
        disablePortal: true,
        modifiers: { flip: { boundariesElement: 'scrollParent' } }
      }}
      arrow
    >
      {children}
    </Tooltip>
  )
}
